import React, { useContext, useState } from 'react'
import { Box, TextField, Button, Typography, FormControl, InputLabel, Select, MenuItem } from '@mui/material'
import { DataGrid } from '@mui/x-data-grid'
import Sidebar from './sidebar'
import CustomToolbar from './CustomToolbarPrint'
import DataContext from '../context/context'
import ProjectFilter from './ProjectFilter'
import { collection, getDocs } from 'firebase/firestore'
import { db } from '../firebase'
import { toNum, yesNo, inDateRange, matchesProject } from '../utils/reportUtils'

export default function CrimeStatsReport() {
  const dropDownData = useContext(DataContext)
  const [date1, setdate1] = useState('')
  const [date2, setdate2] = useState('')
  const [projectFilter, setprojectFilter] = useState([])
  const [prisonFilter, setprisonFilter] = useState([])
  const [probationFilter, setprobationFilter] = useState([])
  const [prisonMin, setprisonMin] = useState('')
  const [offencesMin, setoffencesMin] = useState('')
  const [rows, setRows] = useState([])
  const [rows1, setRows1] = useState([])
  const [convictionTypes, setconvictionTypes] = useState([])
  const [summaryColumns, setSummaryColumns] = useState([
    { field: 'total', headerName: 'Total Beneficiaries', width: 190, sortable: false, filterable: false, disableColumnMenu: true },
    { field: 'convictions_total', headerName: 'Total Convictions Entered', width: 220, sortable: false, filterable: false, disableColumnMenu: true },
  ])

  const handleProjects = (projects) => {
    setprojectFilter(projects)
    setRows([])
    setRows1([])
  }

  const baseColumns = [
    { field: 'firstName', headerName: 'Name', width: 140 },
    { field: 'surName', headerName: 'Surname', width: 140 },
    { field: 'prisonLeaver', headerName: 'Prison Leaver', width: 140 },
    { field: 'howMany', headerName: 'How Many Times', width: 150 },
    { field: 'probation', headerName: 'Probation', width: 120 },
    { field: 'numberOfOffences', headerName: 'Number of Offences', width: 170 },
    { field: 'convictionCount', headerName: 'Convictions Entered', width: 160 },
    { field: 'convictionTypes', headerName: 'Conviction Types (Count)', width: 300 },
  ]

  const columns = [
    ...baseColumns,
    ...convictionTypes.map((type, index) => ({
      field: `conv_${index}`,
      headerName: type,
      width: 130,
    })),
    { field: 'convictionDetails', headerName: 'Conviction Details', width: 320 },
  ]

  const getData = async () => {
    if (projectFilter.length === 0) {
      setRows([])
      setRows1([])
      return
    }
    const q = collection(db, 'employee')
    const querySnapshot = await getDocs(q)

    const convictionOptions = new Set(
      (Array.isArray(dropDownData?.OffendingHistoryConvictionType)
        ? dropDownData.OffendingHistoryConvictionType
        : []
      )
        .map((type) => (typeof type === 'string' ? type.trim() : type))
        .filter(Boolean)
    )
    const details = []

    querySnapshot.forEach((document) => {
      const e = document.data()
      const prisonLeaver = yesNo(e.prison)
      const probation = yesNo(e.probation)
      const howMany = toNum(e.howMany)
      const numberOfOffences = toNum(e.numberOfOffences)
      const convictions = Array.isArray(e.convication) ? e.convication : []
      const typeCounts = {}
      convictions.forEach((c) => {
        const rawType = c?.type
        const type = (typeof rawType === 'string' ? rawType.trim() : rawType) || 'Unspecified'
        convictionOptions.add(type)
        typeCounts[type] = (typeCounts[type] || 0) + 1
      })
      const convictionTypesText = Object.entries(typeCounts)
        .map(([type, count]) => `${type} ×${count}`)
        .join(', ')
      const convictionDetails = convictions
        .map((c) => (typeof c?.details === 'string' ? c.details.trim() : c?.details))
        .filter(Boolean)
        .join(', ')

      if (!inDateRange(e.startDate, date1, date2)) return
      if (!matchesProject(e.gender, projectFilter)) return
      if (prisonFilter.length > 0 && !prisonFilter.includes(prisonLeaver)) return
      if (probationFilter.length > 0 && !probationFilter.includes(probation)) return
      if (prisonMin !== '' && howMany <= toNum(prisonMin)) return
      if (offencesMin !== '' && numberOfOffences <= toNum(offencesMin)) return

      details.push({
        id: document.id,
        firstName: e.firstName || '',
        surName: e.surName || '',
        prisonLeaver,
        howMany,
        probation,
        numberOfOffences,
        convictionCount: convictions.length,
        convictionTypes: convictionTypesText,
        convictionDetails,
        convictionList: Object.keys(typeCounts),
        typeCounts,
      })
    })

    const activeTypes = Array.from(
      new Set(details.flatMap((d) => Object.keys(d.typeCounts)))
    ).sort()
    details.forEach((d) => {
      activeTypes.forEach((type, index) => {
        d[`conv_${index}`] = d.typeCounts[type] || 0
      })
    })
    setconvictionTypes(activeTypes)

    const prisonYes = details.filter((d) => d.prisonLeaver === 'Yes').length
    const prisonNo = details.filter((d) => d.prisonLeaver === 'No').length
    const prisonUnspecified = details.length - prisonYes - prisonNo
    const probationYes = details.filter((d) => d.probation === 'Yes').length
    const probationNo = details.filter((d) => d.probation === 'No').length
    const probationUnspecified = details.length - probationYes - probationNo

    const summaryMetrics = [
      { field: 'total', label: 'Total Beneficiaries', count: details.length },
      { field: 'prison_yes', label: 'Prison Leaver: Yes', count: prisonYes },
      { field: 'prison_no', label: 'Prison Leaver: No', count: prisonNo },
      ...(prisonUnspecified > 0
        ? [{ field: 'prison_unspecified', label: 'Prison Leaver: Not specified', count: prisonUnspecified }]
        : []),
      { field: 'probation_yes', label: 'Probation: Yes', count: probationYes },
      { field: 'probation_no', label: 'Probation: No', count: probationNo },
      ...(probationUnspecified > 0
        ? [{ field: 'probation_unspecified', label: 'Probation: Not specified', count: probationUnspecified }]
        : []),
      {
        field: 'convictions_total',
        label: 'Total Convictions Entered',
        count: details.reduce((total, d) => total + d.convictionCount, 0),
      },
    ]
    const summaryTypes = Array.from(
      new Set([...convictionOptions, ...activeTypes])
    ).filter(Boolean).sort()
    summaryTypes.forEach((type, index) => {
      summaryMetrics.push({
        field: `conviction_${index}`,
        label: `Conviction: ${type}`,
        count: details.reduce((total, d) => total + (d.typeCounts[type] || 0), 0),
      })
    })

    setSummaryColumns(
      summaryMetrics.map(({ field, label }) => ({
        field,
        headerName: label,
        width: Math.max(170, label.length * 9 + 40),
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
      }))
    )
    setRows1([
      Object.fromEntries([
        ['id', 'summary'],
        ...summaryMetrics.map((metric) => [metric.field, metric.count]),
      ]),
    ])
    setRows(details)
  }

  return (
    <Box sx={{ display: 'flex' }}>
      <Sidebar />
      <Box component="main" sx={{ flexGrow: 1, p: 3 }} style={{ marginTop: '50px' }}>
        <div style={{ width: '100%' }}>
          <ProjectFilter value={projectFilter} onChange={handleProjects} />
          <TextField type="date" InputLabelProps={{ shrink: true }} className="addform" label="Start Date" variant="outlined" style={{ width: '45%' }} onChange={(e) => setdate1(e.target.value)} value={date1} />
          <TextField type="date" InputLabelProps={{ shrink: true }} className="addform" label="End Date" variant="outlined" style={{ width: '45%' }} onChange={(e) => setdate2(e.target.value)} value={date2} />

          <FormControl className="addform" style={{ width: '45%', marginTop: '20px' }}>
            <InputLabel id="prison-filter-label">Prison Leaver</InputLabel>
            <Select multiple labelId="prison-filter-label" id="prison-filter" label="Prison Leaver" value={prisonFilter} onChange={(e) => setprisonFilter(e.target.value)} renderValue={(s) => s.join(', ')}>
              <MenuItem value="Yes">Yes</MenuItem>
              <MenuItem value="No">No</MenuItem>
            </Select>
          </FormControl>
          <TextField type="number" className="addform" label="How Many Times in Prison (greater than)" variant="outlined" style={{ width: '45%', marginTop: '20px' }} onChange={(e) => setprisonMin(e.target.value)} value={prisonMin} />

          <FormControl className="addform" style={{ width: '45%', marginTop: '20px' }}>
            <InputLabel id="probation-filter-label">Probation</InputLabel>
            <Select multiple labelId="probation-filter-label" id="probation-filter" label="Probation" value={probationFilter} onChange={(e) => setprobationFilter(e.target.value)} renderValue={(s) => s.join(', ')}>
              <MenuItem value="Yes">Yes</MenuItem>
              <MenuItem value="No">No</MenuItem>
            </Select>
          </FormControl>
          <TextField type="number" className="addform" label="Number of Offences (greater than)" variant="outlined" style={{ width: '45%', marginTop: '20px' }} onChange={(e) => setoffencesMin(e.target.value)} value={offencesMin} />

          <Button onClick={getData} disabled={projectFilter.length === 0} className="addform" variant="contained" style={{ marginBottom: '10px', width: '100%', height: '50px', marginTop: '30px', backgroundColor: '#F21D2F' }}>Search</Button>

          <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>Overall Summary</Typography>
          <div style={{ height: 200, marginBottom: '20px' }}>
            <DataGrid rows={rows1} columns={summaryColumns} hideFooter components={{ Toolbar: CustomToolbar }} />
          </div>

          <Typography variant="h6" sx={{ mb: 1 }}>Breakdown</Typography>
          <div style={{ height: 440 }}>
            <DataGrid rows={rows} columns={columns} pageSize={10} rowsPerPageOptions={[5]} components={{ Toolbar: CustomToolbar }} />
          </div>
        </div>
      </Box>
    </Box>
  )
}
